import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import { useAccountsQuery, useAccountsSummaryQuery, useDeleteAccountMutation, useUsersQuery } from '../../../services/api';
import { CHMAP, CHCOLOR } from '../../../constants/channels';
import { inr, inrShort, dfmtLong, personSub } from '../../../utils/format';
import { KpiGrid } from '../../../components/common/KpiCard';
import TableCard from '../../../components/common/TableCard';
import DataCard from '../../../components/common/DataCard';
import AppButton from '../../../components/common/AppButton';
import Pagination from '../../../components/common/Pagination';
import QueryState from '../../../components/common/QueryState';
import { KpiSkeleton, TableSkeleton } from '../../../components/common/Shimmer';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useToast } from '../../../components/common/Toast';
import { ReadOnlyNotice } from '../../../components/common/Access';
import { usePermissions } from '../../../features/auth/permissions';
import { CONTROL_SX, PillSelect } from '../../../components/common/FormField';
import { SectionHeading, ConditionChip, OwnerAvatar, FootNote } from '../../../components/common/Bits';
import MetricsModal from './MetricsModal';

const LIMIT = 12;

export default function AccountsTab({ channelId }) {
  const toast = useToast();
  const { canEdit, role } = usePermissions();
  // account owners are teammates, so the filter offers the active team
  const { data: team } = useUsersQuery({ active: 'true', limit: 200 });
  const owners = (team?.items || []).map((u) => u.name);
  const [search, setSearch] = useState('');
  const [owner, setOwner] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [metrics, setMetrics] = useState(null);
  const [confirm, setConfirm] = useState(null);

  // Debounced so typing a name is one request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setTerm(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // A page 4 in one channel means nothing in the next.
  useEffect(() => { setPage(1); }, [owner]);
  useEffect(() => { setPage(1); setSearch(''); setOwner(''); }, [channelId]);

  const listArgs = useMemo(
    () => ({ channel: channelId, search: term || undefined, owner: owner || undefined, page, limit: LIMIT }),
    [channelId, term, owner, page]
  );

  const listQuery = useAccountsQuery(listArgs);
  const summaryQuery = useAccountsSummaryQuery({ channel: channelId });
  const [deleteAccount] = useDeleteAccountMutation();

  const isD2C = channelId === 'd2c';
  const s = summaryQuery.data;

  const remove = (a) =>
    setConfirm({
      name: a.name,
      run: async () => {
        await deleteAccount(a.id).unwrap();
        toast('Account removed');
      },
    });

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      await c.run();
    } catch (e) {
      toast(e?.data?.error || e?.message || "Couldn't remove that account", 'error');
    }
  };

  return (
    <>
      {!canEdit && <ReadOnlyNotice role={role} what="the account book" />}
      {s ? (
        <KpiGrid
          columns={4}
          items={[
            { label: 'Total Accounts', value: s.total, sub: 'Converted from leads', accent: CHCOLOR[channelId] },
            { label: 'Est. Monthly Value', value: inrShort(s.monthlyValue), sub: 'Combined across accounts', accent: 'var(--s4)' },
            { label: 'Est. Annual Run-rate', value: inrShort(s.annualRunRate), sub: 'Monthly × 12', accent: 'var(--s3)' },
            { label: 'Avg / Account', value: inrShort(s.avgPerAccount), sub: 'Monthly value', accent: 'var(--s1)' },
          ]}
        />
      ) : (
        <KpiSkeleton columns={4} />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', m: '6px 0 12px', flexWrap: 'wrap', gap: 1.25 }}>
        <SectionHeading sx={{ m: 0 }}>Accounts · {CHMAP[channelId].name}</SectionHeading>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <InputBase
            sx={{ ...CONTROL_SX, width: { xs: '100%', sm: 220 } }}
            placeholder="Search name, contact, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <PillSelect
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            options={[{ value: '', label: 'Any owner' }, ...owners]}
            sx={{ minWidth: 140 }}
          />
        </Box>
      </Box>

      <QueryState query={listQuery} skeleton={<TableSkeleton columns={isD2C ? 9 : 8} rows={8} title={false} />}>
        {(data) =>
          data.items.length ? (
            <>
              <TableCard>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{isD2C ? 'Patient' : 'Account'}</th>
                      <th>{isD2C ? 'Gender · Age' : 'Contact'}</th>
                      <th>Phone</th>
                      <th>Location</th>
                      {isD2C && <th>Known conditions</th>}
                      <th>Owner</th>
                      <th className="num">Est. monthly</th>
                      <th>Customer since</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((a) => {
                      const loc = [a.city, a.state].filter(Boolean).join(', ');
                      return (
                        <tr key={a.id}>
                          <td><b>{a.name}</b></td>
                          <td style={{ color: 'var(--ink-2)' }}>{(isD2C ? personSub(a) : a.contact) || '—'}</td>
                          <td className="code">{a.phone || '—'}</td>
                          <td style={{ color: 'var(--ink-2)' }}>
                            {loc || '—'} {a.pincode && <span className="code">{a.pincode}</span>}
                          </td>
                          {isD2C && (
                            <td>{a.conditions?.length ? a.conditions.map((c) => <ConditionChip key={c}>{c}</ConditionChip>) : '—'}</td>
                          )}
                          <td>
                            <OwnerAvatar name={a.owner} />{' '}
                            <span style={{ color: 'var(--ink-2)', fontSize: 12 }}>{a.owner || '—'}</span>
                          </td>
                          <td className="num"><b>{inr(a.value || 0)}</b></td>
                          <td style={{ color: 'var(--ink-2)' }}>{a.since ? dfmtLong(a.since) : '—'}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <AppButton mini tone="primary" onClick={() => setMetrics(a)}>📊 Business Metrics</AppButton>{' '}
                            {canEdit && <AppButton mini tone="danger" onClick={() => remove(a)}>✕</AppButton>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableCard>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ fontSize: 12, color: 'var(--muted)' }}>
                  {data.total} account{data.total === 1 ? '' : 's'}
                  {term ? ` matching “${term}”` : ''}
                  {owner ? ` owned by ${owner}` : ''}
                </Box>
                <Pagination page={data.page} pages={data.pages} onChange={setPage} />
              </Box>
            </>
          ) : (
            <DataCard>
              {term
                ? `No accounts match “${term}”.`
                : 'No accounts yet. Win a lead in the Leads tab and click “→ Account” to convert it here.'}
            </DataCard>
          )
        }
      </QueryState>

      <FootNote>
        Accounts are won leads that became recurring customers. Click <b>Business Metrics</b> for the last 12 months of
        sales, billed against the ledger.
      </FootNote>

      <ConfirmDialog
        open={!!confirm}
        tone="danger"
        title="Remove account"
        message={confirm ? `Remove “${confirm.name}” from the account book?` : ''}
        detail="The ledger keeps its transactions; only the account record goes."
        confirmLabel="Remove"
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirm}
      />

      <MetricsModal open={!!metrics} channelId={channelId} account={metrics} onClose={() => setMetrics(null)} />
    </>
  );
}
